import { useEffect, useMemo, useState } from "react";
import { adminFetch } from "./adminApi";

function Chip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/80">
      {children}
    </span>
  );
}

export default function AdminTransactions() {
  const [tutorId, setTutorId] = useState("");
  const [requestId, setRequestId] = useState("");
  const [type, setType] = useState("ALL");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const pageSize = 20;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (tutorId.trim()) p.set("tutorId", tutorId.trim());
    if (requestId.trim()) p.set("requestId", requestId.trim());
    if (type !== "ALL") p.set("type", type);
    return p.toString();
  }, [page, tutorId, requestId, type]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/transactions?${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load transactions");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const canPrev = page > 1;
  const canNext = page * pageSize < total;

  return (
    <div className="text-white">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-unbounded text-2xl font-bold">Transactions</h2>
          <p className="mt-2 text-white/70">Filter by tutorId / requestId / type.</p>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <input
            value={tutorId}
            onChange={(e) => {
              setTutorId(e.target.value);
              setPage(1);
            }}
            placeholder="tutorId…"
            className="w-full md:w-48 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />
          <input
            value={requestId}
            onChange={(e) => {
              setRequestId(e.target.value);
              setPage(1);
            }}
            placeholder="requestId…"
            className="w-full md:w-48 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          >
            <option value="ALL">All types</option>
            <option value="TOP_UP">TOP_UP</option>
            <option value="REQUEST_FEE">REQUEST_FEE</option>
            <option value="REFUND">REFUND</option>
            <option value="SIGNUP_BONUS">SIGNUP_BONUS</option>
            <option value="ADMIN_ADJUST">ADMIN_ADJUST</option>
          </select>

          <button
            onClick={load}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-sm text-white/70">{loading ? "Loading…" : `Total: ${total}`}</div>
          <div className="flex items-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
            >
              Prev
            </button>
            <Chip>Page {page}</Chip>
            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
            >
              Next
            </button>
          </div>
        </div>

        {err && <div className="px-4 py-3 text-sm text-red-300">{err}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-white/60">
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left">When</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Amount</th>
                <th className="px-4 py-3 text-left">TutorId</th>
                <th className="px-4 py-3 text-left">RequestId</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-white/60">
                    No transactions found
                  </td>
                </tr>
              ) : (
                items.map((tx) => (
                  <tr key={tx.id} className="border-b border-white/10">
                    <td className="px-4 py-3 text-white/80">
                      {new Date(tx.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <Chip>{tx.type}</Chip>
                    </td>
                    <td className="px-4 py-3 text-white/80">{tx.amount}</td>
                    <td className="px-4 py-3 text-white/60 break-all">{tx.tutorId}</td>
                    <td className="px-4 py-3 text-white/60 break-all">{tx.requestId || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
